import { Stack, Typography } from '@mui/material';
import React from 'react';

import {
  Button,
  ButtonSizeProps,
  ButtonVariantProps,
} from '../../../../../components/atoms/Button';

export interface ReportEditDialogFooterProps {
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  /** Optional lower-emphasis action, e.g. sending a test email. */
  secondaryLabel?: string;
  onSecondary?: () => void;
  secondaryDisabled?: boolean;
  /** Transient status text (test-email progress, validation warning). */
  status?: string;
}

/**
 * One footer, three tabs. Each tab keeps its own validation and mutation and
 * renders this — the actions cannot be lifted into the dialog shell without a
 * callback registry, and the tabs must not each invent their own button row.
 *
 * Buttons are the shared atom at its default styling, so they match every other
 * Save button in the app rather than inventing a dialog-local button style.
 */
export const ReportEditDialogFooter: React.FC<ReportEditDialogFooterProps> = ({
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  status,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      px: 2,
      py: 1.5,
      mt: 2,
      borderTop: '1px solid',
      borderColor: 'v4.border',
      bgcolor: 'v4.surface',
      // Sticky pins the row to the bottom of the dialog's scroll region without
      // reaching into the shared Tabs molecule's flex chain.
      position: 'sticky',
      bottom: 0,
    }}
  >
    {secondaryLabel && onSecondary && (
      <Button
        variant={ButtonVariantProps.Secondary}
        size={ButtonSizeProps.Small}
        onClick={onSecondary}
        disabled={secondaryDisabled}
      >
        {secondaryLabel}
      </Button>
    )}
    {/* Announced, not just shown: the test-email result and its failure message
        appear here with no focus change, so a screen reader would miss them. */}
    <Typography
      role="status"
      aria-live="polite"
      variant="caption"
      sx={{ flex: 1, color: 'v4.textMuted' }}
    >
      {status}
    </Typography>
    <Button
      variant={ButtonVariantProps.Primary}
      size={ButtonSizeProps.Small}
      onClick={onPrimary}
      disabled={primaryDisabled}
    >
      {primaryLabel}
    </Button>
  </Stack>
);
